import { Badge } from '../Badge';
import { Popover } from '../Popover';
import { type ReactNode, useMemo } from 'react';

type BadgeItem = {
  label: string;
  startIcon?: ReactNode;
  smallLeftPadding?: boolean;
};

type TagBadgeProps = {
  values: string[] | BadgeItem[];
  isExpanded?: boolean;
  className?: string;
  visibleTags?: number;
};

export const TagBadge = ({ values, isExpanded, className = '', visibleTags = 1 }: TagBadgeProps) => {
  const unifiedValues = useMemo(
    () =>
      (typeof values[0] === 'string'
        ? values.map((x) => {
            return { label: x };
          })
        : values) as BadgeItem[],
    [values],
  );

  if (!unifiedValues.length) {
    return null;
  }

  if (isExpanded) {
    return (
      <div className="flex h-full items-center" data-values={unifiedValues.map(({ label }) => label).join(', ')}>
        <div className={`flex flex-wrap gap-2 py-2 ${className}`}>
          {unifiedValues.map(({ label, startIcon, smallLeftPadding }, index) => (
            <Badge key={`${label}-${index}`} smallLeftPadding={smallLeftPadding} StartIcon={startIcon}>
              {label}
            </Badge>
          ))}
        </div>
      </div>
    );
  }

  const clampedVisible = Math.max(0, visibleTags);
  const visibleValues = unifiedValues.slice(0, clampedVisible);
  const hiddenCount = unifiedValues.length - visibleValues.length;

  if (hiddenCount === 0) {
    return (
      <div
        className={`flex h-full items-center gap-2 ${className}`}
        data-values={unifiedValues.map(({ label }) => label).join(', ')}
      >
        {visibleValues.map(({ label, startIcon, smallLeftPadding }, index) => (
          <Badge key={`${label}-${index}`} limited smallLeftPadding={smallLeftPadding} StartIcon={startIcon}>
            {label}
          </Badge>
        ))}
      </div>
    );
  }

  return (
    <Popover.Root>
      <Popover.Trigger>
        <div
          className={`flex h-full cursor-pointer items-center gap-2 ${className}`}
          data-values={unifiedValues.map(({ label }) => label).join(', ')}
          onClick={(event) => event.stopPropagation()}
        >
          {visibleValues.map(({ label, startIcon, smallLeftPadding }, index) => (
            <Badge key={`${label}-${index}`} limited smallLeftPadding={smallLeftPadding} StartIcon={startIcon}>
              {label}
            </Badge>
          ))}
          <Badge>{`+${hiddenCount}`}</Badge>
        </div>
      </Popover.Trigger>
      <Popover.Content>
        <div
          className={`scrollbar-custom max-h-56 ${unifiedValues.length > 14 ? 'overflow-y-auto' : 'overflow-y-hidden'}`}
        >
          {unifiedValues.map(({ label }, index) => (
            <div className="pr-2" key={`${label}-${index}`}>
              <span className="uppercase">
                {index + 1}. {label}
              </span>
            </div>
          ))}
        </div>
      </Popover.Content>
    </Popover.Root>
  );
};
